import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content, ContentStack, ContentText, ContentColumns } from 'pdfmake/interfaces';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { TimelineEventType, getEventTypeLabel, getEventColor } from '../types/timeline';
import { ReportDraftTextBlock } from '../types/reportDrafts';
import {
    ReportDraftRenderEvent,
    ReportDraftRenderModel,
    formatSwedishDate
} from './reportDraftView';
import { analyticsApi } from '../api/analyticsApi';

// Initialize pdfmake fonts
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs || pdfFonts;

/**
 * Sanitize filename for cross-platform compatibility
 */
function sanitizeFilename(name: string): string {
    return name
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 100);
}

/**
 * Format date for PDF display (uses Swedish locale)
 */
function formatDateForPdf(date: Date, inexactDate?: boolean): string {
    if (inexactDate) {
        return date.getFullYear().toString();
    }
    return formatSwedishDate(date);
}

/**
 * Validate that a base64 data URL is a supported image format for pdfmake
 * pdfmake only supports JPEG and PNG formats
 */
function isValidPdfImage(dataUrl: string): boolean {
    if (!dataUrl || typeof dataUrl !== 'string') return false;
    if (!dataUrl.startsWith('data:image/')) return false;
    const supportedFormats = ['data:image/jpeg', 'data:image/png', 'data:image/jpg'];
    return supportedFormats.some(format => dataUrl.toLowerCase().startsWith(format));
}

/**
 * Fetch image from Firebase Storage as base64 data URL for embedding in PDF.
 * Uses the getStorageImageBase64 Cloud Function to bypass CORS.
 */
export async function fetchStorageImageAsBase64(storagePath: string): Promise<string | null> {
    try {
        const functions = getFunctions(undefined, 'europe-north1');
        const getStorageImageBase64Fn = httpsCallable<{ storagePath: string }, { base64: string; contentType: string }>(
            functions,
            'getStorageImageBase64'
        );
        const result = await getStorageImageBase64Fn({ storagePath });
        const base64 = result.data.base64;
        if (!isValidPdfImage(base64)) {
            console.warn('Unsupported image format for PDF, skipping:', storagePath,
                base64 ? base64.substring(0, 50) + '...' : 'empty');
            return null;
        }
        return base64;
    } catch (error) {
        console.error('Error fetching image from storage:', storagePath, error);
        return null;
    }
}

/** Paragraph content for a draft text block; null for blank blocks (skipped). */
function formatTextBlock(block: ReportDraftTextBlock): Content | null {
    if (!block.text.trim()) return null;
    return {
        text: block.text,
        style: 'paragraph',
        margin: [0, 0, 0, 10]
    } as ContentText;
}

/**
 * Format a single render-model event as PDF content. All draft curation
 * (overrides, hidden fields, photo exclusions, captions) is already resolved
 * in the render model — this function only prints what it is given.
 */
function formatEventForPdf(
    rendered: ReportDraftRenderEvent,
    imageDataMap: Map<string, string>
): Content {
    const eventColor = getEventColor(rendered.item.eventType);
    const eventTypeLabel = getEventTypeLabel(rendered.item.eventType);

    const eventContent: Content[] = [
        {
            text: eventTypeLabel.toUpperCase(),
            style: 'eventBadge',
            color: eventColor,
            margin: [0, 0, 0, 4]
        } as ContentText
    ];

    if (rendered.fields.description) {
        eventContent.push({
            text: rendered.fields.description,
            style: 'eventTitle',
            margin: [0, 0, 0, 4]
        } as ContentText);
    }

    if (rendered.fields.buildingName) {
        eventContent.push({
            text: `Byggnad: ${rendered.fields.buildingName}`,
            style: 'eventDetail',
            color: '#666666',
            margin: [0, 0, 0, 4]
        } as ContentText);
    }

    if (rendered.fields.completionComment) {
        eventContent.push({
            text: rendered.fields.completionComment,
            style: 'eventDetail',
            italics: true,
            margin: [0, 0, 0, 4]
        } as ContentText);
    }

    if (rendered.fields.measurement) {
        eventContent.push({
            text: rendered.fields.measurement,
            style: 'eventDetail',
            margin: [0, 0, 0, 4]
        } as ContentText);
    }

    // Photos in a 2-column grid (excluded photos are omitted)
    const loadedImages: { base64: string; caption?: string }[] = [];
    for (const photo of rendered.photos) {
        if (photo.excluded) continue;
        const base64 = imageDataMap.get(photo.storagePath);
        if (base64 && base64.startsWith('data:image/')) {
            loadedImages.push({ base64, caption: photo.caption });
        }
    }

    for (let i = 0; i < loadedImages.length; i += 2) {
        const row: Content[] = [];
        for (const img of loadedImages.slice(i, i + 2)) {
            const stack: Content[] = [{ image: img.base64, width: 150 }];
            if (img.caption) {
                stack.push({ text: img.caption, style: 'imageCaption', margin: [0, 2, 0, 0] } as ContentText);
            }
            row.push({ stack } as ContentStack);
        }
        eventContent.push({
            columns: row,
            columnGap: 10,
            margin: [0, 8, 0, 4]
        } as ContentColumns);
    }

    if (rendered.comment && rendered.comment.trim()) {
        eventContent.push({
            text: rendered.comment,
            style: 'eventNote',
            italics: true,
            margin: [0, 8, 0, 0]
        } as ContentText);
    }

    return {
        stack: eventContent,
        style: 'eventCard',
        margin: [0, 0, 0, 12]
    } as ContentStack;
}

export interface ReportPdfMeta {
    reportTitle: string;
    /** Generation timestamp shown in the header; defaults to now. */
    generatedAt?: Date;
}

/** Period label from the model's years, e.g. "2025" or "2024-2026". */
function periodText(years: number[]): string {
    if (years.length === 0) return '';
    const start = years[0];
    const end = years[years.length - 1];
    return start === end ? `${start}` : `${start}-${end}`;
}

/**
 * Pure docDefinition builder — no Firebase, no download. Renders the draft
 * render model: document intro blocks, year intro blocks, event cards with
 * resolved fields/photos/comments, and after-event blocks. Excluded events are
 * skipped, along with their after-event blocks.
 */
export function buildTimelineDocDefinition(
    model: ReportDraftRenderModel,
    imageDataMap: Map<string, string>,
    meta: ReportPdfMeta
): TDocumentDefinitions {
    const includedByYear = model.years
        .map(year => ({ ...year, events: year.events.filter(e => e.included) }))
        .filter(year => year.events.length > 0 || year.blocks.length > 0);

    const years = includedByYear.map(y => y.year);
    const includedEvents = includedByYear.flatMap(y => y.events);

    // Distinct buildings among included events (snapshot key, name fallback)
    const buildingKeys = new Set(includedEvents.map(e =>
        e.item.buildingKey ?? e.item.fields?.buildingName?.original ?? e.eventId));
    const eventTypeCounts = new Map<TimelineEventType, number>();

    for (const rendered of includedEvents) {
        eventTypeCounts.set(rendered.item.eventType, (eventTypeCounts.get(rendered.item.eventType) || 0) + 1);
    }

    const content: Content[] = [
        { text: meta.reportTitle, style: 'header' },
        { text: `Period: ${periodText(years)}`, style: 'subheader' },
        { text: `Genererad: ${formatSwedishDate(meta.generatedAt || new Date())}`, style: 'subheader', margin: [0, 0, 0, 20] }
    ];

    // Document intro paragraphs
    for (const block of model.documentBlocks) {
        const paragraph = formatTextBlock(block);
        if (paragraph) {
            content.push(paragraph);
        }
    }

    // Summary section
    content.push({ text: 'SAMMANFATTNING', style: 'sectionHeader' });
    content.push({
        text: `${includedEvents.length} händelser i ${buildingKeys.size} ${buildingKeys.size === 1 ? 'byggnad' : 'byggnader'}`,
        margin: [0, 5, 0, 5]
    });
    content.push({
        columns: Array.from(eventTypeCounts.entries()).map(([type, count]) => ({
            text: `${getEventTypeLabel(type)}: ${count}`,
            width: 'auto',
            margin: [0, 0, 15, 0]
        })),
        margin: [0, 0, 0, 20]
    } as ContentColumns);

    // Year sections
    includedByYear.forEach((year, yearIndex) => {
        content.push({
            text: year.year.toString(),
            style: 'yearHeader',
            pageBreak: yearIndex > 0 ? 'before' : undefined
        } as ContentText);

        content.push({
            canvas: [{
                type: 'line',
                x1: 0, y1: 0,
                x2: 515, y2: 0,
                lineWidth: 1,
                lineColor: '#e0e0e0'
            }],
            margin: [0, 5, 0, 15]
        } as Content);

        // Year intro paragraphs
        for (const block of year.blocks) {
            const paragraph = formatTextBlock(block);
            if (paragraph) {
                content.push(paragraph);
            }
        }

        // Events grouped by date label (already chronological in the model)
        let currentDateLabel: string | null = null;
        for (const rendered of year.events) {
            const dateLabel = formatDateForPdf(rendered.date, rendered.item.inexactDate);
            if (dateLabel !== currentDateLabel) {
                currentDateLabel = dateLabel;
                content.push({
                    text: dateLabel,
                    style: 'dateHeader',
                    margin: [0, 0, 0, 10]
                } as ContentText);
            }

            content.push(formatEventForPdf(rendered, imageDataMap));

            // After-event paragraphs
            for (const block of rendered.blocksAfter) {
                const paragraph = formatTextBlock(block);
                if (paragraph) {
                    content.push(paragraph);
                }
            }
        }
    });

    return {
        content,
        styles: {
            header: { fontSize: 22, bold: true, margin: [0, 0, 0, 5] },
            subheader: { fontSize: 11, color: '#666666', margin: [0, 0, 0, 3] },
            sectionHeader: { fontSize: 13, bold: true, margin: [0, 15, 0, 8] },
            yearHeader: { fontSize: 18, bold: true, margin: [0, 10, 0, 5] },
            dateHeader: { fontSize: 12, bold: true, color: '#333333', margin: [0, 15, 0, 8] },
            eventCard: { margin: [0, 0, 0, 12] },
            eventBadge: { fontSize: 9, bold: true },
            eventTitle: { fontSize: 11, bold: true },
            eventDetail: { fontSize: 10 },
            imageCaption: { fontSize: 9, color: '#666666', italics: true },
            eventNote: { fontSize: 10, color: '#555555', background: '#f5f5f5', italics: true },
            paragraph: { fontSize: 10, color: '#333333' }
        },
        defaultStyle: { fontSize: 10 },
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40]
    };
}

export interface ReportDraftExportOptions {
    model: ReportDraftRenderModel;
    reportTitle: string;
    /** Progress callback - receives percentage 0-100 */
    onProgress?: (percent: number) => void;
}

/**
 * Export a report draft render model to PDF: fetch photos, build, download.
 */
export async function exportReportDraftToPDF(options: ReportDraftExportOptions): Promise<void> {
    const { model, reportTitle, onProgress } = options;

    const includedEvents = model.years.flatMap(y => y.events).filter(e => e.included);
    if (includedEvents.length === 0) {
        throw new Error('Inga händelser att exportera');
    }

    // Step 1: collect storage paths for non-excluded photos of included events
    onProgress?.(5);
    const allStoragePaths = includedEvents.flatMap(e =>
        e.photos.filter(p => !p.excluded).map(p => p.storagePath)
    );

    // Step 2: fetch images as base64 (the slow part: 5% → 95%)
    const imageDataMap = new Map<string, string>();
    if (allStoragePaths.length === 0) {
        onProgress?.(95);
    } else {
        for (let i = 0; i < allStoragePaths.length; i++) {
            const base64 = await fetchStorageImageAsBase64(allStoragePaths[i]);
            if (base64) {
                imageDataMap.set(allStoragePaths[i], base64);
            }
            onProgress?.(5 + Math.round(((i + 1) / allStoragePaths.length) * 90));
        }
    }

    // Step 3: build document definition
    onProgress?.(97);
    const docDefinition = buildTimelineDocDefinition(model, imageDataMap, { reportTitle });

    // Step 4: download
    onProgress?.(98);
    const years = model.years.map(y => y.year);
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `${reportTitle}-${periodText(years)}-${dateStr}.pdf`;
    pdfMake.createPdf(docDefinition).download(sanitizeFilename(filename));

    analyticsApi.trackReportGenerated('timeline_pdf');
    onProgress?.(100);
}
